/**
 * The client's approval page (FR-E15, e2e-filing B-09; ADR-0037 ND-E7).
 *
 * **Public, and deliberately not `api.ts`.** The client never signs in to ProGST (ADR-0002 —
 * the firm is the user, and this app has no login screen): the token in the link is the whole
 * capability, scoped by the service to one period and one workbook hash. So these calls carry
 * no bearer and no `X-Fiveox-Org` — `api.ts` adds both, and a client's browser holds neither.
 * The service refuses in sentences (used, expired, superseded); the page shows them as sent.
 */

import { SERVICE_API } from './config'
import { type FetchedFile, fileNameFrom } from './files'

/** What the page shows: the client's name and the month, and nothing else of theirs. */
export interface ClientApprovalView {
  client_name: string
  return_name: string
  month: string
  workbook_filename: string
  /** DD-MM-YYYY — the last day the link works. */
  expires_on: string
  points_raised: number
  points_left: number
}

export interface ClientActionResult {
  outcome: 'approved' | 'point_raised'
  message: string
  points_left: number
}

/** A refusal, in the service's own sentence. */
export class LinkRefused extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason: string | undefined,
  ) {
    super(message)
    this.name = 'LinkRefused'
  }

  /** Nothing more can be done through this link — used, superseded, expired, not a link. */
  get final(): boolean {
    return this.status === 404 || ['used', 'superseded', 'expired'].includes(this.reason ?? '')
  }
}

const FALLBACK = 'The approval page could not be reached. Try again in a moment.'

function linkPath(token: string): string {
  return `${SERVICE_API}/api/v1/client-approval/${encodeURIComponent(token)}`
}

async function refusalOf(response: Response): Promise<LinkRefused> {
  try {
    const body = await response.json()
    return new LinkRefused(body?.error?.message ?? FALLBACK, response.status, body?.error?.details?.reason)
  } catch {
    return new LinkRefused(FALLBACK, response.status, undefined)
  }
}

async function call<T>(token: string, init?: RequestInit): Promise<T> {
  // `credentials: 'omit'`: nothing of the firm's session may ride a client's request.
  const response = await fetch(linkPath(token), { credentials: 'omit', ...init })
  if (!response.ok) throw await refusalOf(response)
  return (await response.json()).data as T
}

export const clientApproval = {
  view: (token: string) => call<ClientApprovalView>(token),

  approve: (token: string, name: string) =>
    call<ClientActionResult>(token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'approve', name: name.trim() }),
    }),

  raisePoint: (token: string, note: string, name: string) =>
    call<ClientActionResult>(token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'raise_point', note: note.trim(), name: name.trim() || null }),
    }),

  /** The workbook's bytes — the very file whose hash the link approves. */
  workbook: async (token: string): Promise<FetchedFile> => {
    // Spelled out rather than built on `linkPath`, so the seam check sees the route is called.
    const url = `${SERVICE_API}/api/v1/client-approval/${encodeURIComponent(token)}/workbook`
    const response = await fetch(url, { method: 'POST', credentials: 'omit' })
    if (!response.ok) throw await refusalOf(response)
    return {
      blob: await response.blob(),
      filename: fileNameFrom(response.headers.get('Content-Disposition')),
      sha256: response.headers.get('X-Content-Sha256'),
      render: null,
    }
  },
}

/** A name to approve under: two characters at least, the service's own rule. */
export function canApprove(name: string): boolean {
  return name.split(/\s+/).join(' ').trim().length >= 2
}

/** A point worth sending: three characters at least, and points left to raise. */
export function canRaise(note: string, pointsLeft: number): boolean {
  return note.trim().length >= 3 && pointsLeft > 0
}

/** The page's heading — `GSTR-1 for July 2026`, never a slug or `07-2026`. */
export function heading(view: Pick<ClientApprovalView, 'return_name' | 'month'>): string {
  return `${view.return_name} for ${view.month}`
}
